import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

interface WizardLayoutProps {
  title: string;
  subtitle?: string;
  currentStep: number;
  totalSteps: number;
  children: React.ReactNode;
  onNext?: () => void;
  onBack?: () => void;
  canNext?: boolean;
  isSubmitting?: boolean;
  nextLabel?: string;
  backLabel?: string;
}

const WizardLayout: React.FC<WizardLayoutProps> = ({
  title,
  subtitle,
  currentStep,
  totalSteps,
  children,
  onNext,
  onBack,
  canNext = true,
  isSubmitting = false,
  nextLabel = 'Next',
  backLabel = 'Back'
}) => {
  // Calculate progress percentage
  const progress = ((currentStep) / totalSteps) * 100;
  
  // Ref for the scrollable container
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll to top whenever the step changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [currentStep]);

  return (
    <div className="h-screen bg-gray-50 flex flex-col pt-[80px] md:pt-[88px] overflow-hidden">
      <div className="w-full max-w-3xl mx-auto flex flex-col flex-1 h-full px-4 sm:px-6 lg:px-8 pb-4 md:pb-8 gap-4 md:gap-6">
        
        {/* Progress Bar (Always at top) */}
        <div className="flex-shrink-0 w-full">
          <div className="flex justify-between text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
            <span>Step {currentStep} of {totalSteps}</span>
            <span>{Math.round(progress)}% of your dream trip ready</span>
          </div>
          <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-primary-600 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            />
          </div>
        </div>

        {/* Content Card (Flex-1 to fill available space) */}
        <motion.div 
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 flex flex-col flex-1 min-h-0"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className="px-6 py-4 md:px-8 md:pt-8 md:pb-6 border-b border-gray-100 bg-white z-10 flex-shrink-0">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">{title}</h1>
            {subtitle && <p className="text-sm md:text-lg text-gray-600">{subtitle}</p>}
          </div>

          {/* Body (Scrollable) */}
          <div 
            ref={scrollContainerRef}
            className="flex-1 px-6 py-4 md:px-8 md:py-6 overflow-y-auto custom-scrollbar relative"
          >
             <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="h-full"
             >
               {children}
             </motion.div>
          </div>

          {/* Footer (Fixed at bottom of card) */}
          <div className="px-6 py-4 md:px-8 md:py-6 bg-gray-50 border-t border-gray-100 flex justify-between items-center flex-shrink-0">
            <button
              onClick={onBack}
              disabled={currentStep === 1 || isSubmitting}
              className={`px-4 py-2 md:px-6 md:py-3 rounded-xl font-medium text-gray-700 transition-colors ${
                currentStep === 1 
                  ? 'opacity-0 cursor-default' 
                  : 'hover:bg-gray-200 bg-gray-100'
              }`}
            >
              {backLabel}
            </button>
            
            <button
              onClick={onNext}
              disabled={!canNext || isSubmitting}
              className={`px-6 py-2 md:px-8 md:py-3 rounded-xl font-semibold text-white shadow-lg transition-all transform flex items-center gap-2 ${
                !canNext || isSubmitting
                  ? 'bg-gray-300 cursor-not-allowed shadow-none'
                  : 'bg-primary-600 hover:bg-primary-700 hover:scale-[1.02] active:scale-[0.98]'
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {nextLabel}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </motion.div>
        
        {/* Navigation Dots (Fixed at bottom of screen) */}
        <div className="flex-shrink-0 flex justify-center gap-2 pb-2 md:pb-0">
          {Array.from({ length: totalSteps }).map((_, idx) => (
            <div 
              key={idx}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                idx + 1 === currentStep ? 'bg-primary-600' : idx + 1 < currentStep ? 'bg-primary-300' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WizardLayout;
